import type { JSX } from 'react';
import { useState } from 'react';
import { Alert, Badge, Button, Center, Group, Loader, Paper, Progress, SegmentedControl, Select, SimpleGrid, Stack, Table, Text, TextInput } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { PortalId, PortalJobView } from '@pst/shared';
import { ApiError, portalJobsApi } from '../api';
import { FETCH_PORTALS, PORTAL_ALLOW, PORTAL_JOB_KINDS, PORTAL_JOB_STATES, PORTAL_JOB_TARGETS, formatDateTime } from '../format';
import { Section } from '../ui/Section';
import { PortalRound } from './PortalRound';

const message = (e: unknown): string => (e instanceof ApiError || e instanceof Error ? e.message : 'Fehlgeschlagen.');
/** The worker writes its sign of life every minute; after ten without one it counts as not running. */
const STALE_MS = 10 * 60_000;

/**
 * Einstellungen › Abzüge (ADR 0023): whether the server's worker runs, how
 * much of today's limit each portal used, and the jobs with their outcome.
 */
export function PortalJobsPanel(): JSX.Element {
  const queryClient = useQueryClient();
  const view = useQuery({ queryKey: ['portal-jobs'], queryFn: portalJobsApi.overview, refetchInterval: 10_000 });
  const cancel = useMutation({ mutationFn: portalJobsApi.cancel, onSettled: () => queryClient.invalidateQueries({ queryKey: ['portal-jobs'] }) });
  const phone = useMediaQuery('(max-width: 48em)');
  if (view.isPending) return <Center h={120}><Loader size="sm" /></Center>;
  if (view.isError) return <Alert color="red">{message(view.error)}</Alert>;
  const { enabled, worker, usage, rounds, jobs } = view.data;
  if (!enabled) return <Alert color="gray">Abzüge aus Portalen sind in dieser Installation abgeschaltet.</Alert>;
  const running = worker && Date.now() - Date.parse(worker.at) < STALE_MS;
  return (
    <Stack gap="md" maw={1100}>
      <Section title="Abzugsdienst" description="Der Dienst am Server nimmt je Minute höchstens einen Auftrag: Probeanmeldungen und Einzelabfragen sofort, alles andere nur im Zeitfenster der Nacht. Bei jedem Warnzeichen hält er an und stoppt das Portal."
        actions={<Badge variant="light" color={running ? 'green' : 'orange'}>{running ? 'läuft' : worker ? 'kein Lebenszeichen' : 'nicht eingerichtet'}</Badge>}>
        <Stack gap="sm">
          <Text size="sm" c="dimmed">
            {worker ? `Letztes Lebenszeichen ${formatDateTime(worker.at)}${worker.version ? `, Stand ${worker.version}` : ''}.` : 'Noch kein Lebenszeichen – der Nutzer-Timer pst-portale ist am Server nicht eingerichtet.'}
          </Text>
          <SimpleGrid cols={{ base: 1, sm: 3 }}>
            {usage.map((u) => {
              const share = u.dailyLimit ? Math.min(100, (u.pagesToday / u.dailyLimit) * 100) : 0;
              return (
                <Paper key={u.portal} withBorder p="sm" radius="md">
                  <Text size="sm" fw={600}>{FETCH_PORTALS[u.portal].label}</Text>
                  <Text size="xs" c="dimmed">{u.dailyLimit ? `heute ${u.pagesToday} von ${u.dailyLimit} Seitenaufrufen` : 'kein Zugang eingetragen'}</Text>
                  <Progress mt={6} size="sm" value={share} color={share >= 80 ? 'orange' : 'brand'} aria-label={`Tageslimit ${FETCH_PORTALS[u.portal].label}`} />
                </Paper>
              );
            })}
          </SimpleGrid>
        </Stack>
      </Section>
      <Section title="Nachtumlauf" description="Je Portal ein Häppchen pro Nacht im Zeitfenster: zuerst die Liste der Hersteller bzw. Seiten, dann die Einträge mit dem ältesten Abrufdatum zuerst. Der Abzugsdienst führt Häppchen erst aus, wenn er sie für das Portal beherrscht.">
        <SimpleGrid cols={{ base: 1, md: 3 }}>
          {rounds.map((r) => <PortalRound key={r.portal} round={r} showTitle />)}
        </SimpleGrid>
      </Section>
      <NewJob />
      <Section title="Aufträge" description="Die letzten 200 Aufträge, neueste zuerst. Ein wartender Auftrag endet beim Abbrechen sofort, ein laufender nach dem aktuellen Seitenaufruf.">
        {cancel.isError && <Alert color="red" py={6}>{message(cancel.error)}</Alert>}
        {jobs.length === 0 ? <Text size="sm" c="dimmed">Noch keine Aufträge. Eine Probeanmeldung entsteht beim Speichern der Zugangsdaten unter Verbindungen.</Text> : phone ? (
          <Stack gap="xs">
            {jobs.map((j) => <JobCard key={j.id} job={j} onCancel={() => cancel.mutate(j.id)} cancelling={cancel.isPending && cancel.variables === j.id} />)}
          </Stack>
        ) : (
          <Table.ScrollContainer minWidth={760}>
            <Table verticalSpacing={4} highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Angelegt</Table.Th><Table.Th>Portal</Table.Th><Table.Th>Art</Table.Th><Table.Th>Zustand</Table.Th>
                  <Table.Th ta="right">Seiten</Table.Th><Table.Th>Ergebnis</Table.Th><Table.Th />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {jobs.map((j) => <JobRow key={j.id} job={j} onCancel={() => cancel.mutate(j.id)} cancelling={cancel.isPending && cancel.variables === j.id} />)}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        )}
      </Section>
    </Stack>
  );
}

function JobRow({ job, onCancel, cancelling }: { job: PortalJobView; onCancel: () => void; cancelling: boolean }): JSX.Element {
  const state = PORTAL_JOB_STATES[job.state];
  const open = job.state === 'neu' || job.state === 'läuft';
  return (
    <Table.Tr>
      <Table.Td><Text size="sm">{formatDateTime(job.createdAt)}</Text>{job.createdBy && <Text size="xs" c="dimmed">{job.createdBy}</Text>}</Table.Td>
      <Table.Td><Text size="sm">{FETCH_PORTALS[job.portal].label}</Text></Table.Td>
      <Table.Td><Text size="sm">{PORTAL_JOB_KINDS[job.kind]}</Text>{job.target && <Text size="xs" c="dimmed">{job.target}</Text>}</Table.Td>
      <Table.Td><Badge variant="light" color={state.color}>{state.label}</Badge>{job.cancelRequested && open && <Text size="xs" c="dimmed">Abbruch angefordert</Text>}</Table.Td>
      <Table.Td ta="right"><Text size="sm">{job.pages}{job.downloads ? ` · ${job.downloads} Dateien` : ''}</Text></Table.Td>
      <Table.Td style={{ maxWidth: 360 }}><Text size="xs" style={{ overflowWrap: 'anywhere' }}>{job.message ?? (job.finishedAt ? '' : '–')}</Text></Table.Td>
      <Table.Td>{open && !job.cancelRequested && <Button size="xs" variant="subtle" color="gray" loading={cancelling} onClick={onCancel}>Abbrechen</Button>}</Table.Td>
    </Table.Tr>
  );
}

/** The same job on a phone: one card instead of a wide row. */
function JobCard({ job, onCancel, cancelling }: { job: PortalJobView; onCancel: () => void; cancelling: boolean }): JSX.Element {
  const state = PORTAL_JOB_STATES[job.state];
  const open = job.state === 'neu' || job.state === 'läuft';
  return (
    <Paper withBorder p="sm" radius="md">
      <Group justify="space-between" align="flex-start" wrap="nowrap" gap="xs">
        <Stack gap={0} style={{ minWidth: 0 }}>
          <Text size="sm" fw={600}>{FETCH_PORTALS[job.portal].label} · {PORTAL_JOB_KINDS[job.kind]}</Text>
          <Text size="xs" c="dimmed">{formatDateTime(job.createdAt)}{job.createdBy ? ` · ${job.createdBy}` : ''}</Text>
        </Stack>
        <Badge variant="light" color={state.color} style={{ flexShrink: 0 }}>{state.label}</Badge>
      </Group>
      {job.target && <Text size="xs" mt={4}>{job.target}</Text>}
      <Text size="xs" mt={4} style={{ overflowWrap: 'anywhere' }}>{job.pages} Seiten{job.downloads ? ` · ${job.downloads} Dateien` : ''}{job.message ? ` · ${job.message}` : ''}</Text>
      {open && !job.cancelRequested && <Button mt={6} size="xs" variant="subtle" color="gray" loading={cancelling} onClick={onCancel}>Abbrechen</Button>}
      {job.cancelRequested && open && <Text size="xs" c="dimmed">Abbruch angefordert</Text>}
    </Paper>
  );
}

/** A job by hand, for the portals that have an adapter: now, or in the night window. */
function NewJob(): JSX.Element {
  const queryClient = useQueryClient();
  const portals = (Object.keys(PORTAL_JOB_TARGETS) as PortalId[]);
  const [portal, setPortal] = useState<PortalId>(portals[0]!);
  const [kind, setKind] = useState<'einzel' | 'auftrag'>('auftrag');
  const [target, setTarget] = useState('');
  const create = useMutation({
    mutationFn: () => portalJobsApi.create({ portal, kind, target: target.trim() }),
    onSuccess: () => setTarget(''),
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['portal-jobs'] }),
  });
  const info = PORTAL_JOB_TARGETS[portal];
  return (
    <Section title="Neuer Auftrag" description={`${info?.hint ?? ''} Baupreislexikon folgt.`}>
      <Stack gap="sm">
        <Group align="flex-end" gap="sm" wrap="wrap">
          <Select label="Portal" w={200} value={portal} onChange={(v) => v && setPortal(v as PortalId)} allowDeselect={false} comboboxProps={{ withinPortal: true }}
            data={portals.map((p) => ({ value: p, label: FETCH_PORTALS[p].label }))} />
          <TextInput label={info?.label} placeholder={info?.placeholder} value={target} onChange={(e) => setTarget(e.currentTarget.value)} style={{ flex: '1 1 320px' }} />
        </Group>
        <Group gap="sm" wrap="wrap">
          <SegmentedControl size="xs" value={kind} onChange={(v) => setKind(v as 'einzel' | 'auftrag')}
            data={[{ value: 'auftrag', label: 'nachts im Zeitfenster' }, { value: 'einzel', label: 'sofort' }]} />
          <Button size="xs" disabled={!target.trim()} loading={create.isPending} onClick={() => create.mutate()}>Auftrag anlegen</Button>
          <Text size="xs" c="dimmed">Zählt gegen das Tageslimit; braucht „{PORTAL_ALLOW.label}“ und eine gelungene Probeanmeldung.</Text>
        </Group>
        {create.isError && <Alert color="red" py={6}>{message(create.error)}</Alert>}
        {create.isSuccess && <Alert color="green" py={6}>Auftrag angelegt{kind === 'einzel' ? ' – er läuft in der Regel innerhalb von 1–2 Minuten.' : ' – er läuft in der nächsten Nacht.'}</Alert>}
      </Stack>
    </Section>
  );
}
